import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Col, Row } from 'reactstrap';
import moment from 'moment';
import { themeQuartz, colorSchemeDark, colorSchemeLight } from 'ag-grid-community';
import BudgetAccountSection from './BudgetAccountSection';
import BudgetForecastSection from './BudgetForecastSection';
import BudgetGroupsTable from './BudgetGroupsTable';
import BudgetGrowthTable from './BudgetGrowthTable';
import BudgetEventsTable from './BudgetEventsTable';
import BudgetItemsTable from './BudgetItemsTable';
import BudgetGroupNavTabs from './NavTabs';
import BudgetAccountSettingsModal from './form/BudgetAccountSettingsModal';
import RetailerSelectionGrid from './RetailerSelectionGrid';
import config from '../../../config';
import ChartBuilder from '../../../shared/components/chart/ChartBuilder';
import CardPanel from '../../../shared/components/widgets/CardPanel';
import { history } from '../../../helpers';
import { getBudgetAccountByRetailer } from '../shared/budgetApi';
import { getChartTheme } from '../../../shared/components/chart/chartThemeHelper';

// Platform configuration - easy to extend for new platforms
const PLATFORM_CONFIG = {
  amazon: {
    name: 'Amazon',
    label: 'Amazon (Ads)',
    key: 'amazon',
    prefix: 'amz',
    endpoints: {
      all: (sellerSelect, startDate, endDate) => `${config.serverUrl}/amazon/api/bgd/stats-by-dates-breakdown?accountId=${sellerSelect.value}&parentAccountId=${sellerSelect.parentAccountId}&startDate=${startDate}&endDate=${endDate}`,
      catalog: () => `${config.serverUrl}/catalogs`,
    },
    hasGraphTypeSelector: true,
    graphTypes: [
      { value: 'total', label: 'Total' },
      { value: 'sp', label: 'Sponsored Products', spend: 'sp_spend', sales: 'sp_sales' },
      { value: 'sd', label: 'Sponsored Display', spend: 'sd_spend', sales: 'sd_sales' },
      { value: 'sb', label: 'Sponsored Brands', spend: 'sb_spend', sales: 'sb_sales' },
      // { value: 'sbv', label: 'Sponsored Brand Video', spend: 'sbv_spend', sales: 'sbv_sales' },
      { value: 'st', label: 'Sponsored Television', spend: 'st_spend', sales: 'st_sales' },
      { value: 'dsp', label: 'DSP', spend: 'dsp_spend', sales: 'dsp_sales' },
    ],
  },
  google: {
    name: 'Google',
    label: 'Google',
    key: 'google',
    prefix: 'google',
    endpoints: {
      all: (sellerSelect, startDate, endDate) => `${config.serverUrl}/google/api/bgd/stats-by-dates-breakdown?accountId=${sellerSelect.value}&parentAccountId=${sellerSelect.parentAccountId}&startDate=${startDate}&endDate=${endDate}`,
      catalog: (sellerSelect) => `${config.serverUrl}/google/catalogs/${sellerSelect.value}`,
    },
    hasGraphTypeSelector: false,
    graphTypes: [
      { value: 'total', label: 'Total' },
    ],
  },
  meta: {
    name: 'Meta',
    label: 'Meta',
    key: 'meta',
    prefix: 'meta',
    endpoints: {
      all: (sellerSelect, startDate, endDate) => `${config.serverUrl}/meta/api/bgd/stats-by-dates-breakdown?accountId=${sellerSelect.value}&parentAccountId=${sellerSelect.parentAccountId}&startDate=${startDate}&endDate=${endDate}`,
      catalog: (sellerSelect) => `${config.serverUrl}/meta/catalogs/${sellerSelect.value}`,
    },
    hasGraphTypeSelector: false,
    graphTypes: [
      { value: 'total', label: 'Total' },
    ],
  },
  tiktok: {
    name: 'TikTok',
    label: 'TikTok',
    key: 'tiktok',
    prefix: 'tiktok',
    endpoints: {
      all: (sellerSelect, startDate, endDate) => `${config.serverUrl}/tiktok/api/bgd/stats-by-dates-breakdown?accountId=${sellerSelect.value}&parentAccountId=${sellerSelect.parentAccountId}&startDate=${startDate}&endDate=${endDate}`,
      catalog: (sellerSelect) => `${config.serverUrl}/tiktok/catalogs/${sellerSelect.value}`,
    },
    hasGraphTypeSelector: false,
    graphTypes: [
      { value: 'total', label: 'Total' },
    ],
  },
  walmart: {
    name: 'Walmart',
    label: 'Walmart',
    key: 'walmart',
    prefix: 'wm',
    endpoints: {
      all: (sellerSelect, startDate, endDate) => `${config.serverUrl}/walmart/api/bgd-stats-by-dates-breakdown?accountId=${sellerSelect.value}&parentAccountId=${sellerSelect.parentAccountId}&startDate=${startDate}&endDate=${endDate}`,
      catalog: (sellerSelect) => `${config.serverUrl}/walmart/catalogs/${sellerSelect.value}`,
    },
    hasGraphTypeSelector: false,
    graphTypes: [
      { value: 'total', label: 'Total' },
    ],
  },
};

export default function BudgetGraph() {
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const location = useLocation();

  // Unified state for current platform data
  const [platformData, setPlatformData] = useState({
    allGraphData: [{}],
    allGraphLoad: false,
    allSales: 0,
    allSpend: 0,
    allById: [],
    allReportDays: 0,
    allError: null,
    internalGraphData: [{}],
    internalGraphLoad: false,
    internalSales: 0,
    internalSpend: 0,
    internalById: [],
    internalReportDays: 0,
    internalError: null,
    combinedGraphData: [{ all: {}, internal: {} }],
  });

  const [forceRefresh, setForceRefresh] = useState(moment().utc());
  const [budgetAccountData, setBudgetAccountData] = useState({});
  const [retailerAccountData, setRetailerAccountData] = useState({});
  const [showAccountSettingsModal, setShowAccountSettingsModal] = useState(false);
  const [budgetGroupOptions, setBudgetGroupOptions] = useState([]); // For Events (with ad types)
  const [budgetGroupOptionsForForecast, setBudgetGroupOptionsForForecast] = useState([]); // For Forecast (grouped, CSV)
  const [budgetGroupOptionsForItems, setBudgetGroupOptionsForItems] = useState([]); // For Items (grouped, CSV)
  const [budgetGroupCounts, setBudgetGroupCounts] = useState({}); // Budget group counts by platform
  const [retailView, setRetailView] = useState(() => {
    // Initialize from URL query parameter, default to null (show selection grid)
    const params = new URLSearchParams(location.search);
    return params.get('retailer') || null;
  });
  const [graphType, setGraphType] = useState('total');
  const [series, setSeries] = useState([
    {
      type: 'line',
      xKey: 'report_date',
      yKey: 'internal.spend',
      yName: 'Spend',
    },
    {
      type: 'line',
      xKey: 'report_date',
      yKey: 'internal.sales',
      yName: 'Sales',
    },
  ]);
  const [reportStartDate] = useState(moment().startOf('month').format('YYYYMMDD'));
  const [reportEndDate] = useState(moment().format('YYYYMMDD'));
  const [activeTab, setActiveTab] = useState((location.hash || 'Groups').replace('#', ''));

  const activeDayOptions = [
    { label: 'Monday', name: 'monday', value: '0' },
    { label: 'Tuesday', name: 'tuesday', value: '1' },
    { label: 'Wednesday', name: 'wednesday', value: '2' },
    { label: 'Thursday', name: 'thursday', value: '3' },
    { label: 'Friday', name: 'friday', value: '4' },
    { label: 'Saturday', name: 'saturday', value: '5' },
    { label: 'Sunday', name: 'sunday', value: '6' },
  ];

  const retailerOptions = [
    {
      optgroup: {
        label: 'RETAIL:',
        options: [
          { label: 'Amazon', value: 'amazon' },
          { label: 'Walmart', value: 'walmart' },
          { label: 'Target', value: 'target' },
          { label: 'Google', value: 'google' },
          { label: 'Microsoft', value: 'microsoft' },
          // { label: 'WooCommerce (Store)', value: 'woocommerce_store' },
          // { label: 'Criteo', value: 'criteo' },
          // { label: 'TheTradeDesk', value: 'thetradedesk' },
        ],
      },
    },
    {
      optgroup: {
        label: 'SOCIAL MEDIA:',
        options: [
          { label: 'Meta', value: 'meta' },
          { label: 'TikTok', value: 'tiktok' },
          { label: 'LinkedIn', value: 'linkedin' },
        ],
      },
    },
    {
      optgroup: {
        label: 'DELIVERY:',
        options: [
          { label: 'Instacart', value: 'instacart' },
          { label: 'DoorDash', value: 'doordash' },
          { label: 'Uber', value: 'uber' },
          { label: 'GoPuff', value: 'gopuff' },
        ],
      },
    },
    {
      optgroup: {
        label: 'PHARMACY:',
        options: [
          { label: 'CVS', value: 'cvs' },
          { label: 'Walgreens', value: 'walgreens' },
          { label: 'Rite Aid', value: 'rite_aid' },
        ],
      },
    },
    {
      optgroup: {
        label: 'SPECIALTY RETAIL:',
        options: [
          { label: 'Chewy', value: 'chewy' },
          { label: 'Macy\'s', value: 'macys' },
          { label: 'Nordstroms', value: 'nordstroms' },
          { label: 'The Home Depot', value: 'the_home_depot' },
          { label: 'Ace Hardware', value: 'ace_hardware' },
          { label: 'Michaels', value: 'michaels' },
          { label: 'Hobby Lobby', value: 'hobby_lobby' },
          { label: 'Sephora', value: 'sephora' },
        ],
      },
    },
    {
      optgroup: {
        label: 'GROCERY:',
        options: [
          { label: 'Kroger', value: 'kroger' },
          { label: 'Albertsons', value: 'albertsons' },
          { label: 'Meijer', value: 'meijer' },
          { label: 'Whole Foods', value: 'whole_foods' },
          { label: 'Publix', value: 'publix' },
        ],
      },
    },
    {
      optgroup: {
        label: 'NETWORK:',
        options: [
          { label: 'YouTube', value: 'youtube' },
          { label: 'YouTube TV', value: 'youTube_tv' },
          { label: 'Disney+', value: 'disney_plus' },
          { label: 'Apple+', value: 'apple_plus' },
          { label: 'Paramount+', value: 'paramount_plus' },
          { label: 'Peacock', value: 'peacock' },
          { label: 'Netflix', value: 'netflix' },
          { label: 'Tubo', value: 'tubo' },
          { label: 'Freevee', value: 'freevee' },
          { label: 'SKY', value: 'sky' },
        ],
      },
    },
  ];

  // Generic data fetching function that works for any platform
  // Only sellerSelect.value is tracked in dependencies to prevent unnecessary rerenders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getPlatformData = useCallback((platformKey, isMounted) => {
    const platformConfig = PLATFORM_CONFIG[platformKey];
    if (!platformConfig) {
      console.error(`Platform configuration not found for: ${platformKey}`);
      return;
    }

    // Reset loading states
    if (isMounted()) {
      setPlatformData(prev => ({
        ...prev,
        allGraphLoad: true,
        allGraphData: [{}],
        allSales: 0,
        allSpend: 0,
        allById: [],
        internalGraphLoad: true,
        internalGraphData: [{}],
        internalSales: 0,
        internalSpend: 0,
        internalById: [],
      }));
    }

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    // Unified BGD API: Single API call that returns all, internal, and external data
    const url = platformConfig.endpoints.all(sellerSelect, reportStartDate, reportEndDate);
    fetch(url, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      })
      .then((data) => {
        // Transform new API structure to match existing state expectations
        const byDate = data?.byDate || [];
        const budgetGroupDetail = data?.budgetGroups?.detail || {};

        // Transform reportDate (camelCase) to report_date for compatibility
        // AND add internal_spend_ and internal_sales_ fields for sparklines
        const transformedByDate = byDate.map(item => {
          const transformed = {
            ...item,
            report_date: item.reportDate,
          };

          // For each budget group, add internal_ prefixed fields
          // The new API has spend_BudgetName and sales_BudgetName but NOT internal_spend_BudgetName
          // We need to add those fields based on the budgetGroups.detail data
          Object.keys(budgetGroupDetail).forEach(groupName => {
            const spendKey = `spend_${groupName}`;
            const salesKey = `sales_${groupName}`;
            const ordersKey = `orders_${groupName}`;

            // If this date has data for this budget group
            if (item[spendKey] !== undefined) {
              const groupDetail = budgetGroupDetail[groupName];

              // Check if this group is 100% internal (has internal data and NO external data)
              // If external_spend is 0, then all spend in byDate is internal
              const isInternalOnly = groupDetail
                                      && groupDetail.internal_spend > 0
                                      && groupDetail.external_spend === 0;

              if (isInternalOnly) {
                // This group is 100% internal - the spend/sales fields represent internal data
                transformed[`internal_spend_${groupName}`] = item[spendKey];
                transformed[`internal_sales_${groupName}`] = item[salesKey];
                if (item[ordersKey] !== undefined) {
                  transformed[`internal_orders_${groupName}`] = item[ordersKey];
                }
              } else {
                // This group has external data (or mixed/no data) - set internal fields to 0
                transformed[`internal_spend_${groupName}`] = 0;
                transformed[`internal_sales_${groupName}`] = 0;
                transformed[`internal_orders_${groupName}`] = 0;
              }
            }
          });

          return transformed;
        });

        // Build by_id structure from budgetGroups.detail
        const normalizedById = {
          group: new Proxy(budgetGroupDetail, {
            get: (target, prop) => target[prop] || {},
          }),
        };

        // Extract totals for all and internal
        const allTotals = data?.totals?.all || { total_sales: 0, total_spend: 0 };
        const internalTotals = data?.totals?.internal || { total_sales: 0, total_spend: 0 };

        // Sort by date
        const sortedByDate = transformedByDate.sort((a, b) => {
          const checkPos = (b.report_date > a.report_date) ? -1 : 0;
          return (b.report_date < a.report_date) ? 1 : checkPos;
        });

        // Update state with both all and internal data from single response
        setPlatformData(prev => ({
          ...prev,
          // All data
          allSales: allTotals.total_sales || 0,
          allSpend: allTotals.total_spend || 0,
          allById: normalizedById,
          allReportDays: sortedByDate.length,
          allGraphLoad: false,
          allGraphData: sortedByDate.length > 0 ? sortedByDate : [{}],
          allError: null,
          // Internal data (same byDate, same byId, different totals)
          internalSales: internalTotals.total_sales || 0,
          internalSpend: internalTotals.total_spend || 0,
          internalById: normalizedById,
          internalReportDays: sortedByDate.length,
          internalGraphLoad: false,
          internalGraphData: sortedByDate.length > 0 ? sortedByDate : [{}],
          internalError: null,
        }));
      })
      .catch((error) => {
        console.warn(`${platformConfig.name} unified BGD data not available:`, error.message);
        // Set empty data but don't break the page
        setPlatformData(prev => ({
          ...prev,
          allGraphLoad: false,
          allGraphData: [{}],
          allSales: 0,
          allSpend: 0,
          allById: { group: {} },
          allReportDays: 0,
          allError: error.message,
          internalGraphLoad: false,
          internalGraphData: [{}],
          internalSales: 0,
          internalSpend: 0,
          internalById: { group: {} },
          internalReportDays: 0,
          internalError: error.message,
        }));
      });
  }, [sellerSelect.value, reportStartDate, reportEndDate, user.jwtToken]);

  const setHashTag = (tab) => {
    if (tab) {
      history.push({
        pathname: location.pathname,
        search: location.search, // Preserve query parameters
        hash: `#${tab}`,
      });
    }
  };

  const getBudgetAccountDaily = useCallback(() => {
    setBudgetAccountData({});

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const budgetAccountDailyUrl = `${config.serverUrl}/accounts/budget-account-daily?accountId=${sellerSelect.value}&retailer=${retailView}`;
    fetch(budgetAccountDailyUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      })
      .then((data) => {
        setBudgetAccountData(data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [sellerSelect.value, retailView, user.jwtToken]);

  // Fetch Budget Group data for dropdown options (used across multiple tabs)
  const getBudgetGroupData = useCallback(() => {
    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const retailerPath = `${retailView.toLowerCase()}/`;
    const budgetUrl = `${config.serverUrl}/accounts/${retailerPath}budget-group-daily?accountId=${sellerSelect.value}&platform=limitless`;

    fetch(budgetUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      })
      .then((data) => {
        const options = [];
        const forecastOptions = [];

        data.budgetGroupData.forEach(bg => {
          // Check if we have the new CSV field with multiple IDs
          if (bg.budget_group_daily_id_csv && bg.ad_type_csv) {
            const ids = bg.budget_group_daily_id_csv.split(',');

            // For Amazon: use hardcoded ad type groups, for others: use API ad_type_csv or split it
            let adTypes;
            if (retailView === 'amazon') {
              // Amazon uses hardcoded order
              const AD_TYPE_GROUPS_ORDER = ['SP,SD', 'SB', 'ST'];
              adTypes = AD_TYPE_GROUPS_ORDER;
            } else {
              // Non-Amazon: use actual ad_type_csv from API
              // It could be a single value or comma-separated
              adTypes = bg.ad_type_csv.split(',').map(t => t.trim());
              // If we only have one ad_type but multiple IDs, repeat it for each ID
              if (adTypes.length === 1 && ids.length > 1) {
                adTypes = Array(ids.length).fill(adTypes[0]);
              }
            }

            // Create individual options for Events (with ad type in label for Amazon only)
            // Match IDs with ad types by index position
            ids.forEach((id, index) => {
              const adType = adTypes[index] || adTypes[0] || '';
              // Only show ad_type suffix for Amazon
              const label = retailView === 'amazon'
                ? `${bg.budget_group_name} - ${adType}`
                : bg.budget_group_name;

              options.push({
                label,
                value: parseInt(id.trim(), 10),
                adTypeCsv: adType,
              });
            });

            // Create grouped option for Forecast (group name with CSV value)
            forecastOptions.push({
              label: bg.budget_group_name,
              value: bg.budget_group_daily_id_csv, // CSV of all IDs
            });
          } else if (bg.budget_group_daily_id != null) {
            // Fallback for old format (single ID)
            // Skip if budget_group_daily_id is null or undefined
            options.push({
              label: bg.budget_group_name,
              value: bg.budget_group_daily_id,
              adTypeCsv: bg.ad_type_csv || '',
            });
            forecastOptions.push({
              label: bg.budget_group_name,
              value: bg.budget_group_daily_id.toString(),
            });
          }
        });
        setBudgetGroupOptions(options);
        setBudgetGroupOptionsForForecast(forecastOptions);
        setBudgetGroupOptionsForItems(forecastOptions); // Items uses same grouped format as Forecast
      })
      .catch((error) => {
        console.error('Error fetching budget group data:', error);
        setBudgetGroupOptions([]);
      });
  }, [sellerSelect.value, retailView, user.jwtToken]);

  // Fetch retailer-specific account settings
  const getRetailerAccountSettings = useCallback(async () => {
    try {
      const data = await getBudgetAccountByRetailer({
        accountId: sellerSelect.value,
        retailer: retailView,
        jwtToken: user.jwtToken,
      });
      setRetailerAccountData(data);

      // Check if account settings are properly configured for ALL platforms
      const isConfigured = data?.cost_metric_view && data?.terms_approval === 1;
      setShowAccountSettingsModal(!isConfigured);
    } catch (error) {
      console.error('Error fetching retailer account settings:', error);
      // If there's an error or no data, show the modal
      setRetailerAccountData({});
      setShowAccountSettingsModal(true);
    }
  }, [sellerSelect.value, retailView, user.jwtToken]);

  // Fetch budget group counts for all platforms
  const getBudgetGroupCounts = useCallback(async () => {
    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const url = `${config.serverUrl}/accounts/budgetGroupDailySummary?accountId=${sellerSelect.value}`;

    try {
      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        throw Error(response.statusText);
      }
      const data = await response.json();

      // Convert array to object keyed by ad_platform
      const countsMap = {};
      data.forEach(item => {
        countsMap[item.ad_platform] = item.bgd_count;
      });

      setBudgetGroupCounts(countsMap);
    } catch (error) {
      console.error('Error fetching budget group counts:', error);
      setBudgetGroupCounts({});
    }
  }, [sellerSelect.value, user.jwtToken]);

  // Fetch data when seller or platform changes (only if retailer is selected)
  useEffect(() => {
    if (!retailView) return undefined; // Don't fetch data if no retailer is selected

    let isMounted = true;
    const checkMounted = () => isMounted;

    getPlatformData(retailView, checkMounted);
    getBudgetAccountDaily();
    getBudgetGroupData();

    return () => {
      isMounted = false;
    };
  }, [sellerSelect.value, forceRefresh, retailView, getPlatformData, getBudgetAccountDaily, getBudgetGroupData]);

  // Check retailer-specific account settings when retailer changes, account changes, when switching tabs, or on initial load
  useEffect(() => {
    if (!retailView) return; // Don't check settings if no retailer is selected
    getRetailerAccountSettings();
  }, [retailView, sellerSelect.value, forceRefresh, activeTab, getRetailerAccountSettings]);

  // Fetch budget group counts when showing selection grid (no retailer selected)
  useEffect(() => {
    if (!retailView) {
      getBudgetGroupCounts();
    }
  }, [retailView, sellerSelect.value, getBudgetGroupCounts]);

  // Sync retailer state with URL parameter changes (e.g., when clicking sidebar button)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRetailer = params.get('retailer') || null;
    if (urlRetailer !== retailView) {
      setRetailView(urlRetailer);
      if (urlRetailer) {
        setGraphType('total'); // Reset graph type when retailer changes
      }
    }
  }, [location.search, retailView]);

  useEffect(() => {
    const hashTag = location.hash;
    if (hashTag && hashTag.includes('#')) {
      setActiveTab((hashTag || 'Groups').replace('#', ''));
    } else {
      setHashTag('Groups');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Combine all and internal data for chart
  useEffect(() => {
    const combinedGraphData = [];
    platformData.allGraphData.forEach((allRow) => {
      const internalRow = platformData.internalGraphData.find(row => row.report_date === allRow.report_date);
      combinedGraphData.push({
        report_date: allRow.report_date,
        all: allRow,
        internal: internalRow || {},
      });
    });
    setPlatformData(prev => ({ ...prev, combinedGraphData }));
  }, [platformData.allGraphData, platformData.internalGraphData]);

  const updateChartSeries = (type) => {
    const platformConfig = PLATFORM_CONFIG[retailView];
    const graphTypeConfig = platformConfig.graphTypes.find(gt => gt.value === type) || { value: 'total', label: '' };

    const spendKey = graphTypeConfig.spend || 'spend';
    const salesKey = graphTypeConfig.sales || 'sales';
    const label = graphTypeConfig.label ? `${graphTypeConfig.label} ` : '';

    setSeries([
      {
        type: 'line',
        xKey: 'report_date',
        yKey: `internal.${spendKey}`,
        yName: `${label}Spend`,
      },
      {
        type: 'line',
        xKey: 'report_date',
        yKey: `internal.${salesKey}`,
        yName: `${label}Sales`,
      },
    ]);
  };

  const graphChange = (e) => {
    const newGraphType = e.target.value;
    setGraphType(newGraphType);
    updateChartSeries(newGraphType);
  };

  const retailerChange = (e) => {
    const newRetailView = e.target.value;
    setRetailView(newRetailView);
    setGraphType('total'); // Reset to default graph type
    updateChartSeries('total');

    // Update URL query parameter to persist retailer selection on refresh
    const params = new URLSearchParams(location.search);
    params.set('retailer', newRetailView);
    history.replace({
      pathname: location.pathname,
      search: `?${params.toString()}`,
      hash: location.hash, // Preserve the active tab hash
    });
  };

  // Handler for retailer selection from grid
  const handleRetailerSelect = (retailerValue) => {
    setRetailView(retailerValue);
    setGraphType('total');

    // Update URL query parameter
    const params = new URLSearchParams(location.search);
    params.set('retailer', retailerValue);
    history.push({
      pathname: location.pathname,
      search: `?${params.toString()}`,
      hash: '#Groups', // Default to Groups tab
    });
  };

  // Use new Quartz theme with Theming API (no CSS imports needed!)
  const isDarkMode = theme.className && !theme.className.endsWith('light');
  const gridTheme = isDarkMode
    ? themeQuartz.withPart(colorSchemeDark)
    : themeQuartz.withPart(colorSchemeLight);
  const currentPlatformConfig = PLATFORM_CONFIG[retailView];

  const chartOptions = currentPlatformConfig ? {
    theme: getChartTheme(theme),
    data: platformData.combinedGraphData,
    series,
    title: {
      text: `${currentPlatformConfig.name} Budget Graph`,
    },
  } : {};

  // If no retailer is selected, show the retailer selection grid
  if (!retailView) {
    const configuredRetailers = Object.keys(PLATFORM_CONFIG);
    return (
      <RetailerSelectionGrid
        retailerOptions={retailerOptions}
        configuredRetailers={configuredRetailers}
        budgetGroupCounts={budgetGroupCounts}
        onRetailerSelect={handleRetailerSelect}
      />
    );
  }

  // Retailer is selected - show the budget interface
  return (
    <Container className="dashboard">
      {/* Budget Account Settings Modal - shown when retailer settings are not configured and not on Account tab */}
      <BudgetAccountSettingsModal
        isOpen={showAccountSettingsModal && activeTab !== 'Account'}
        retailer={retailView}
        retailerConfig={currentPlatformConfig}
        onCancel={() => setHashTag('Account')}
      />

      <Col md={12}>
        <Row>
          <CardPanel showButtons={false}>
            <h4>
              <div style={{ width: '100%', display: 'table-cell' }}>
                Retailer: &nbsp;
                <select onChange={(e) => retailerChange(e)} className="select-options" value={retailView}>
                  {retailerOptions.map((group) => (
                    <optgroup key={group.optgroup.label} label={group.optgroup.label}>
                      {group.optgroup.options.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={!PLATFORM_CONFIG[option.value]}
                        >
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              {currentPlatformConfig.hasGraphTypeSelector && (
                <div style={{
                  width: '100%',
                  display: 'table-cell',
                  justifyContent: 'flex-end',
                  whiteSpace: 'nowrap',
                }}
                >
                  {`${currentPlatformConfig.name} Graph Data:`} &nbsp;
                  <select onChange={(e) => graphChange(e)} className="select-options" value={graphType}>
                    {currentPlatformConfig.graphTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </h4>
          </CardPanel>
        </Row>
      </Col>
      <Col md={12}>
        <Row>
          <CardPanel
            showButtons={false}
            parentRefresh={platformData.allGraphLoad || platformData.internalGraphLoad}
          >
            <ChartBuilder chartOptions={chartOptions} />
          </CardPanel>
        </Row>
      </Col>
      <Col md={12}>
        <Row>
          <BudgetGroupNavTabs
            activeTab={activeTab}
            setActiveTab={setHashTag}
          />
        </Row>
        <Row>
          {activeTab && activeTab === 'Account' && (
            <BudgetAccountSection
              budgetAccountData={retailerAccountData}
              setForceRefresh={setForceRefresh}
              retailer={retailView}
              retailerConfig={currentPlatformConfig}
            />
          )}
          {activeTab && activeTab === 'Groups' && (
            <BudgetGroupsTable
              title={`${currentPlatformConfig.name} Budget`}
              allSpend={platformData.allSpend}
              allSales={platformData.allSales}
              allById={platformData.allById}
              allByDate={platformData.allGraphData}
              allGraphLoad={platformData.allGraphLoad}
              internalSpend={platformData.internalSpend}
              internalSales={platformData.internalSales}
              internalById={platformData.internalById}
              internalByDate={platformData.internalGraphData}
              internalGraphLoad={platformData.internalGraphLoad}
              retailer={currentPlatformConfig.name}
              gridTheme={gridTheme}
              reportDays={{
                allReportDays: platformData.allReportDays,
                internalReportDays: platformData.internalReportDays,
              }}
              forceRefresh={forceRefresh}
              setForceRefresh={setForceRefresh}
              budgetAccountData={budgetAccountData}
              activeDayOptions={activeDayOptions}
              retailerConfig={currentPlatformConfig}
            />
          )}
          {activeTab && activeTab === 'Events' && (
            <BudgetEventsTable
              retailerConfig={currentPlatformConfig}
              forceRefresh={forceRefresh}
              setForceRefresh={setForceRefresh}
              activeDayOptions={activeDayOptions}
              budgetGroupOptions={budgetGroupOptions}
            />
          )}
          {activeTab && activeTab === 'Items' && (
            <BudgetItemsTable
              retailerConfig={currentPlatformConfig}
              forceRefresh={forceRefresh}
              setForceRefresh={setForceRefresh}
              budgetGroupOptions={budgetGroupOptionsForItems}
            />
          )}
          {activeTab && activeTab === 'Growth' && (
            <BudgetGrowthTable
              retailerConfig={currentPlatformConfig}
            />
          )}
          {activeTab && activeTab === 'Forecast' && (
            <BudgetForecastSection
              retailerConfig={currentPlatformConfig}
              budgetGroupOptions={budgetGroupOptionsForForecast}
            />
          )}
        </Row>
      </Col>
    </Container>
  );
}
